import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IconChevron, IconPlus, IconSwap, IconTrash } from "../ui/icons";
import { Button, cx } from "../ui/primitives";
import { DestinationPicker, type BulkDestination } from "./DestinationPicker";

/** Under Tailwind's `sm`: the phone layout — a top strip plus a bottom bar, sheet picker. */
function useNarrow() {
  const q = "(max-width: 639px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

/**
 * The select mode's controls (issue #12), shown while select mode is on.
 *
 *   desktop: one sticky bar along the bottom —
 *            N selected · Select all (total) · Move to collection ▾ · Add to… · Remove · Cancel
 *   phone:   a top strip (Cancel · N selected · Select all) and a bottom bar
 *            (Move / Add to… / Remove), the picker in a bottom sheet.
 *
 * Portalled so `fixed` is the viewport's whatever the page's layout; plain buttons in DOM
 * order, so Tab reaches every one. The page pads its grid while this shows, so the bar
 * never sits over the last row.
 */
export function BulkActionBar({
  count,
  total,
  allSelected,
  collectionId,
  isFaps,
  uploadsSelected,
  busy,
  onSelectAll,
  onClear,
  onMoveOrAdd,
  onRemove,
  onCancel,
}: {
  count: number;
  total: number;
  allSelected: boolean;
  collectionId: string;
  isFaps: boolean;
  uploadsSelected: boolean;
  busy: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onMoveOrAdd: (mode: "move" | "add", to: BulkDestination) => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const narrow = useNarrow();
  const [picking, setPicking] = useState<"move" | "add" | null>(null);
  const none = count === 0;
  const disabled = none || busy;
  // Out of Faps a "remove" is an unfap; say what it does.
  const removeLabel = isFaps ? "Unfap" : "Remove";

  const allToggle = (
    <button
      type="button"
      onClick={allSelected ? onClear : onSelectAll}
      className="rounded-md px-2 py-1 text-xs font-medium text-[var(--color-accent-soft)] hover:bg-[var(--color-panel-2)]"
    >
      {allSelected ? "Clear" : `Select all (${total})`}
    </button>
  );
  const countLabel = (
    <span className="text-sm font-semibold text-zinc-100" aria-live="polite">
      {count} selected
    </span>
  );

  const picker = picking ? (
    <DestinationPicker
      mode={picking}
      excludeId={collectionId}
      uploadsSelected={uploadsSelected}
      sheet={narrow}
      onClose={() => setPicking(null)}
      onPick={(to) => {
        const mode = picking;
        setPicking(null);
        onMoveOrAdd(mode, to);
      }}
    />
  ) : null;

  const actionButtons = (
    <>
      <div className="relative">
        <Button
          variant="primary"
          size={narrow ? "md" : "sm"}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={picking === "move"}
          onClick={() => setPicking(picking === "move" ? null : "move")}
          className={cx(narrow && "h-11 w-full whitespace-nowrap px-2")}
        >
          <IconSwap width={14} height={14} />
          {narrow ? "Move" : "Move to collection"}
          {narrow ? null : <IconChevron width={12} height={12} />}
        </Button>
        {!narrow && picking === "move" ? picker : null}
      </div>
      <div className="relative">
        <Button
          variant="subtle"
          size={narrow ? "md" : "sm"}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={picking === "add"}
          onClick={() => setPicking(picking === "add" ? null : "add")}
          className={cx(narrow && "h-11 w-full whitespace-nowrap px-2")}
        >
          <IconPlus width={14} height={14} />
          Add to…
        </Button>
        {!narrow && picking === "add" ? picker : null}
      </div>
      <Button
        variant="danger"
        size={narrow ? "md" : "sm"}
        disabled={disabled}
        onClick={onRemove}
        className={cx(narrow && "h-11 w-full whitespace-nowrap px-2")}
      >
        <IconTrash width={14} height={14} />
        {removeLabel}
      </Button>
    </>
  );

  // Above the floating Goon Fuel pill (z-40), which sits in the bar's bottom-right corner
  // and would cover Cancel; select mode is short, the pill is back the moment it ends.
  const shell =
    "fixed inset-x-0 border-[var(--color-edge)] bg-[var(--color-panel)]/95 shadow-2xl backdrop-blur";

  return createPortal(
    <>
      {narrow ? (
        <>
          <div
            role="toolbar"
            aria-label="Selection"
            // Over the sticky SiteBar (z-50) it replaces while selecting, the way a photo
            // app's selection header takes the title bar's place.
            className={cx(shell, "top-0 z-[51] flex items-center justify-between gap-2 border-b px-4 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]")}
          >
            <button type="button" onClick={onCancel} className="rounded-md px-2 py-1 text-sm text-zinc-300 hover:bg-[var(--color-panel-2)]">
              Cancel
            </button>
            {countLabel}
            {allToggle}
          </div>
          {/* The sheet (z-40) takes the bottom while it is open; the bar steps aside. */}
          {picking ? null : (
            <div
              role="toolbar"
              aria-label="Bulk actions"
              className={cx(shell, "bottom-0 z-[45] grid grid-cols-3 gap-2 border-t px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]")}
            >
              {actionButtons}
            </div>
          )}
          {picker}
        </>
      ) : (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          className={cx(shell, "bottom-0 z-[45] border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]")}
        >
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2">
            {countLabel}
            <span aria-hidden className="text-zinc-600">·</span>
            {allToggle}
            <div className="ml-auto flex items-center gap-2">
              {actionButtons}
              <Button variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
